/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { RELAY_RESOURCE_TYPE } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { createResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import HydrationRelayQuery, {
	type useHydrationQueryLoaderQuery as HydrationRelayQueryType,
} from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';

export const RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY =
	'queues-jql-builder-hydration-relay';

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const queuesJqlBuilderHydrationQueryRelayResource = createResource<
	PreloadedQuery<HydrationRelayQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY}`,
	getKey: () => RESOURCE_TYPE_QUEUES_JQL_BUILDER_HYDRATION_RELAY,
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const queryReference = loadQuery<HydrationRelayQueryType>(
			getRelayEnvironment(),
			HydrationRelayQuery,
			{
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				isFilter: false,
				jql: '',
				filterAri: '',
				viewContext: null,
				input: { jql: '' },
				isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
			},
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null && QueryPromisesMap.get(queryReference.id)) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});
